import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSite } from '../../context/SiteContext';
import {
  LayoutDashboard, Ticket, LogOut, Home, Settings, BarChart3
} from 'lucide-react';
import { Skeleton } from '../../components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, token, logout } = useAuth();
  const { settings, getImageUrl } = useSite();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const statsRes = await axios.get(`${API}/admin/stats`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const menuItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', active: true },
    { path: '/admin/cupones', icon: Ticket, label: 'Cupones' },
    { path: '/admin/estadisticas', icon: BarChart3, label: 'Estadísticas' },
    { path: '/admin/configuracion', icon: Settings, label: 'Configuración' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-dashboard">
      {/* Sidebar - Desktop only */}
      <aside className="w-64 bg-white border-r border-slate-200 fixed left-0 top-0 bottom-0 z-40 hidden md:flex flex-col">
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <img 
              src={getImageUrl(settings.logo_url)} 
              alt={settings.site_name} 
              className="w-10 h-10 rounded-full object-cover" 
            />
            <div>
              <h2 className="font-fredoka font-bold text-slate-900">{settings.site_name}</h2>
              <p className="text-xs text-slate-500">Panel Admin</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {menuItems.map(({ path, icon: Icon, label, active }) => (
            <Link
              key={path}
              to={path}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                active 
                  ? 'bg-canary text-slate-900 font-semibold' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Icon className="w-5 h-5" />
              {label}
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-100 space-y-2">
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-100 rounded-xl transition-all"
          >
            <Home className="w-5 h-5" />
            Ver Sitio
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-all"
          >
            <LogOut className="w-5 h-5" />
            Cerrar Sesión
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 md:ml-64 p-4 sm:p-6 pb-24 md:pb-6">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between mb-4 bg-white rounded-xl p-3 shadow-sm">
          <div className="flex items-center gap-3">
            <img 
              src={getImageUrl(settings.logo_url)} 
              alt={settings.site_name} 
              className="w-8 h-8 rounded-full object-cover" 
            />
            <h2 className="font-fredoka font-bold text-slate-900 text-sm">{settings.site_name}</h2>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-6 md:mb-8">
          <div>
            <h1 className="font-fredoka text-xl md:text-2xl font-bold text-slate-900">Dashboard</h1>
            <p className="text-slate-600 text-sm md:text-base">Bienvenido, {user?.nombre}</p>
          </div>
        </div>

        {/* Stats - Mobile optimized */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6 mb-6 md:mb-8">
          {loading ? (
            [...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-slate-100">
                <Skeleton className="w-10 h-10 md:w-12 md:h-12 rounded-lg mb-3 md:mb-4" />
                <Skeleton className="h-8 md:h-10 w-16 md:w-20 mb-2" />
                <Skeleton className="h-3 md:h-4 w-20 md:w-24" />
              </div>
            ))
          ) : (
            <>
              <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-slate-100 hover:shadow-md transition-all">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-royal rounded-lg flex items-center justify-center mb-3 md:mb-4">
                  <Ticket className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <p className="font-fredoka text-2xl md:text-3xl font-bold text-slate-900">{stats?.total_coupons || 0}</p>
                <p className="text-slate-500 text-xs md:text-sm">Cupones Activos</p>
              </div>

              <Link 
                to="/admin/cupones"
                className="bg-gradient-to-br from-canary to-yellow-400 rounded-xl p-4 md:p-6 shadow-sm hover:shadow-md transition-all group"
              >
                <div className="w-10 h-10 md:w-12 md:h-12 bg-white/30 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                  <Ticket className="w-5 h-5 md:w-6 md:h-6 text-slate-900" />
                </div>
                <p className="font-fredoka text-base md:text-xl font-bold text-slate-900">Cupones</p>
                <p className="text-slate-700 text-xs md:text-sm hidden sm:block">Agregar, editar o eliminar</p>
              </Link>

              <Link 
                to="/admin/configuracion"
                className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-slate-100 hover:shadow-md transition-all group col-span-2 md:col-span-1"
              >
                <div className="w-10 h-10 md:w-12 md:h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-3 md:mb-4 group-hover:bg-slate-200 transition-colors">
                  <Settings className="w-5 h-5 md:w-6 md:h-6 text-slate-600" />
                </div>
                <p className="font-fredoka text-base md:text-xl font-bold text-slate-900">Configuración</p>
                <p className="text-slate-500 text-xs md:text-sm">Personalizar sitio</p>
              </Link>
            </>
          )}
        </div>

        {/* Quick Actions - Mobile optimized */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 md:p-6">
          <h2 className="font-fredoka text-base md:text-lg font-semibold text-slate-900 mb-3 md:mb-4">Acciones Rápidas</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            <Link
              to="/admin/cupones"
              className="flex items-center gap-3 md:gap-4 p-3 md:p-4 rounded-xl border border-slate-200 hover:border-royal hover:bg-royal/5 transition-all"
            >
              <div className="w-10 h-10 bg-canary rounded-lg flex items-center justify-center flex-shrink-0">
                <Ticket className="w-5 h-5 text-slate-900" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-slate-900 text-sm md:text-base">Nuevo Cupón</p>
                <p className="text-xs md:text-sm text-slate-500 truncate">Agregar cupón de descuento</p>
              </div>
            </Link>
            
            <Link
              to="/"
              className="flex items-center gap-3 md:gap-4 p-3 md:p-4 rounded-xl border border-slate-200 hover:border-royal hover:bg-royal/5 transition-all"
            >
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Home className="w-5 h-5 text-slate-600" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-slate-900 text-sm md:text-base">Ver Sitio</p>
                <p className="text-xs md:text-sm text-slate-500 truncate">Ver como usuario</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Mobile Bottom Navigation */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-2 py-3 z-50 safe-area-inset-bottom">
          <div className="flex justify-around">
            {menuItems.map(({ path, icon: Icon, label, active }) => (
              <Link
                key={path}
                to={path}
                className={`flex flex-col items-center p-2 rounded-lg transition-all ${
                  active ? 'text-royal bg-royal/10' : 'text-slate-500'
                }`}
              >
                <Icon className="w-6 h-6" />
                <span className="text-[10px] mt-1 font-medium">{label}</span>
              </Link>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
